/**
 * PATH: packages/document-editor/src/useDocumentEditor.ts
 * FILE_ID: DOCEDITOR-USE-EDITOR-001
 * PURPOSE: Own TipTap lifecycle and emit JSON-first document change records.
 * WHY: The editor frame stays declarative and domain extensions remain injectable.
 * RELATED: DocumentEditor.tsx, contracts.ts
 */
import { useEffect, useMemo, useRef } from 'react'
import { useEditor } from '@tiptap/react'
import type { Editor } from '@tiptap/core'
import { buildDocumentExtensions, type DocumentExtensionOptions } from './extensions'
import { createDocument, type VersionedDocument } from './contracts'
import { exportDocumentHtml } from './serialization'

export interface DocumentEditorChange {
  document: VersionedDocument
  html: string
}

export interface UseDocumentEditorOptions {
  document: VersionedDocument
  extensionOptions?: DocumentExtensionOptions
  editable?: boolean
  ariaLabel?: string
  onChange?: (change: DocumentEditorChange) => void
}

export function useDocumentEditor(options: UseDocumentEditorOptions): Editor | null {
  const onChangeRef = useRef(options.onChange)
  onChangeRef.current = options.onChange
  const metadataRef = useRef(options.document.metadata)
  metadataRef.current = options.document.metadata
  const extensions = useMemo(
    () => buildDocumentExtensions(options.extensionOptions),
    [options.extensionOptions],
  )
  const editor = useEditor({
    extensions,
    content: options.document.content,
    editable: options.editable ?? true,
    editorProps: {
      attributes: {
        class: 'qdoc-prose',
        role: 'textbox',
        'aria-label': options.ariaLabel ?? 'Document editor',
        'aria-multiline': 'true',
      },
    },
    onUpdate: ({ editor: current }) => {
      const document = createDocument(current.getJSON(), metadataRef.current)
      onChangeRef.current?.({
        document,
        html: exportDocumentHtml(document, { extensions }),
      })
    },
  })

  useEffect(() => {
    if (!editor) return
    editor.setEditable(options.editable ?? true)
  }, [editor, options.editable])

  useEffect(() => {
    if (!editor) return
    const current = JSON.stringify(editor.getJSON())
    const next = JSON.stringify(options.document.content)
    if (current !== next) editor.commands.setContent(options.document.content, { emitUpdate: false })
  }, [editor, options.document.content])

  return editor
}
